import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Activity, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import * as XLSX from 'xlsx';

interface PersonalTrabajando {
  id: string;
  nombre: string;
  apellido: string;
  cargo: string;
  ubicacion: string;
  servicioAsignado: {
    id: string;
    nombre: string;
    categoria: string;
    zonaGestion: string;
  };
  estadoActividad: {
    label: string;
  };
  rut?: string;
  nombres?: string;
}

interface PersonalTrabajandoModalProps {
  isOpen: boolean;
  onClose: () => void;
  personalTrabajando: PersonalTrabajando[];
}

export const PersonalTrabajandoModal: React.FC<PersonalTrabajandoModalProps> = ({
  isOpen,
  onClose,
  personalTrabajando
}) => {

  const [filtroCargo, setFiltroCargo] = useState<string>('');
  const [filtroComuna, setFiltroComuna] = useState<string>('');
  const [paginaActual, setPaginaActual] = useState<number>(1);
  const personasPorPagina = 10;

  const formatRut = (rut?: string) => {
    if (!rut) return '';
    return String(rut).replace(/\./g, '').trim();
  };

  // Obtener listas únicas de cargos y ubicaciones
  const cargosUnicos = useMemo(() => {
    const cargos = personalTrabajando.map(p => p.cargo).filter(Boolean);
    return ['Todos', ...Array.from(new Set(cargos))];
  }, [personalTrabajando]);

  const comunasUnicas = useMemo(() => {
    const comunas = personalTrabajando.map(p => p.ubicacion).filter(Boolean);
    return ['Todas', ...Array.from(new Set(comunas))];
  }, [personalTrabajando]);

  // Filtrar personal según los filtros seleccionados
  const personalFiltrado = useMemo(() => {
    return personalTrabajando.filter(persona => {
      const cumpleCargo = !filtroCargo || filtroCargo === 'Todos' || persona.cargo === filtroCargo;
      const cumpleComuna = !filtroComuna || filtroComuna === 'Todas' || persona.ubicacion === filtroComuna;
      return cumpleCargo && cumpleComuna;
    });
  }, [personalTrabajando, filtroCargo, filtroComuna]);

  // Calcular paginación
  const totalPaginas = Math.ceil(personalFiltrado.length / personasPorPagina);
  const indiceInicio = (paginaActual - 1) * personasPorPagina;
  const indiceFin = indiceInicio + personasPorPagina;
  const personalPaginado = personalFiltrado.slice(indiceInicio, indiceFin);

  // Resetear página al cambiar filtros
  useMemo(() => {
    setPaginaActual(1);
  }, [filtroCargo, filtroComuna]);

  // Exportar lista filtrada a Excel (.xlsx)
  const exportToExcel = () => {
    try {
      const rows = personalFiltrado.map(p => ({
        Nombre: p.nombres || `${p.nombre || ''} ${p.apellido || ''}`.trim(),
        RUT: formatRut(p.rut),
        Cargo: p.cargo || '',
        Comuna: p.ubicacion || ''
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Personal Activo');
      const filename = `personal_activo_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (e) {
      console.error('Error exportando a Excel:', e);
      // Fallback: create CSV blob
      try {
        const csvRows = personalFiltrado.map(p => [
          (p.nombres || `${p.nombre || ''} ${p.apellido || ''}`).trim(),
          formatRut(p.rut),
          p.cargo || '',
          p.ubicacion || ''
        ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
        const header = ['Nombre','RUT','Cargo','Comuna'].join(',');
        const csvContent = [header, ...csvRows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `personal_activo_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (ee) {
        console.error('Export fallback failed:', ee);
      }
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                <Activity className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Personal Activo</h2>
                <p className="text-green-100">
                  {personalFiltrado.length} de {personalTrabajando.length} persona{personalTrabajando.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-green-200 transition-colors p-2 rounded-full hover:bg-white hover:bg-opacity-20"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-gray-600">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">Filtros:</span>
            </div>
            
            {/* Filtro por Cargo */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Cargo:</label>
              <select
                value={filtroCargo}
                onChange={(e) => setFiltroCargo(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {cargosUnicos.map((cargo) => (
                  <option key={cargo} value={cargo === 'Todos' ? '' : cargo}>
                    {cargo}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro por Ubicación */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Comuna:</label>
              <select
                value={filtroComuna}
                onChange={(e) => setFiltroComuna(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {comunasUnicas.map((comuna) => (
                  <option key={comuna} value={comuna === 'Todas' ? '' : comuna}>
                    {comuna}
                  </option>
                ))}
              </select>
            </div>

            {/* Botón limpiar filtros */}
            {(filtroCargo || filtroComuna) && (
              <button
                onClick={() => {
                  setFiltroCargo('');
                  setFiltroComuna('');
                }}
                className="text-sm text-green-600 hover:text-green-700 font-medium"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {personalFiltrado.length === 0 ? (
            <div className="text-center py-16">
              <Activity className="h-20 w-20 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                {personalTrabajando.length === 0 ? 'No hay personal activo' : 'No hay resultados'}
              </h3>
              <p className="text-gray-500">
                {personalTrabajando.length === 0 
                  ? 'Actualmente no hay personas activas en el sistema.'
                  : 'No se encontró personal con los filtros seleccionados.'
                }
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                {/* Tabla estilo Excel */}
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-300">
                      <th className="text-left py-3 px-6 font-semibold text-gray-700 bg-gray-50 text-base">Nombre</th>
                      <th className="text-left py-3 px-6 font-semibold text-gray-700 bg-gray-50 text-base">RUT</th>
                      <th className="text-left py-3 px-6 font-semibold text-gray-700 bg-gray-50 text-base">Cargo</th>
                      <th className="text-left py-3 px-6 font-semibold text-gray-700 bg-gray-50 text-base">Comuna</th>
                    </tr>
                  </thead>
                  <tbody>
                    {personalPaginado.map((persona, index) => (
                      <tr
                        key={persona.id}
                        className={`border-b border-gray-200 hover:bg-green-50 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                      >
                        <td className="py-3 px-6 font-medium text-gray-900">
                          {persona.nombres || `${persona.nombre || ''} ${persona.apellido || ''}`.trim()}
                        </td>
                        <td className="py-3 px-6 text-gray-700">{formatRut(persona.rut) || '—'}</td>
                        <td className="py-3 px-6 text-gray-700">{persona.cargo}</td>
                        <td className="py-3 px-6 text-gray-600">{persona.ubicacion}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación movida al footer para mejor UX (ver footer abajo) */}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-xl border-t">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Mostrando:</span> {indiceInicio + 1} - {Math.min(indiceFin, personalFiltrado.length)} de {personalFiltrado.length}
            </div>

            <div className="flex items-center space-x-4">
              {totalPaginas > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPaginaActual(prev => Math.max(1, prev - 1))}
                    disabled={paginaActual === 1}
                    aria-label="Anterior"
                    title="Anterior"
                    className={`px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center ${
                      paginaActual === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  <div className="flex items-center space-x-1">
                    {(() => {
                      const pageWindowSize = 10;
                      const pageWindowStart = Math.floor((paginaActual - 1) / pageWindowSize) * pageWindowSize + 1;
                      const pageWindowEnd = Math.min(pageWindowStart + pageWindowSize - 1, totalPaginas);
                      const pages = Array.from({ length: pageWindowEnd - pageWindowStart + 1 }, (_, i) => pageWindowStart + i);
                      return pages.map(pagina => (
                        <button
                          key={pagina}
                          onClick={() => setPaginaActual(pagina)}
                          className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                            paginaActual === pagina
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {pagina}
                        </button>
                      ));
                    })()}
                  </div>

                  <button
                    onClick={() => setPaginaActual(prev => Math.min(totalPaginas, prev + 1))}
                    disabled={paginaActual === totalPaginas}
                    aria-label="Siguiente"
                    title="Siguiente"
                    className={`px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center ${
                      paginaActual === totalPaginas
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}

              <button
                onClick={exportToExcel}
                className="px-4 py-2 bg-white border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors font-medium"
              >
                Exportar a Excel
              </button>

              <button
                onClick={onClose}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
